<template>
  <div class="contact-conversation--panel">
    <div v-if="!uiFlags.isFetching" class="contact-conversation__wrap">
      <div v-if="!previousConversations.length" class="no-label-message">
        <span>
          {{ $t('CONTACT_PANEL.CONVERSATIONS.NO_RECORDS_FOUND') }}
        </span>
      </div>
      <div v-else class="contact-conversation--list">
        <div
          v-for="conv in prevConvItems"
          :key="conv.id"
          class="prev-conv"
          @click="navigateToConversation(conv)"
        >
          <span class="prev-dot" :style="{ background: conv.dotColor }" />
          <div class="prev-conv__body">
            <b class="text-truncate">{{ conv.title }}</b>
            <small>{{ conv.meta }}</small>
            <div v-if="conv.labels.length" class="prev-conv__labels-row">
              <div
                :ref="`labels-${conv.id}`"
                class="prev-conv__labels"
                :class="{ 'is-expanded': expandedLabels[conv.id] }"
              >
                <span
                  v-for="label in conv.labels"
                  :key="label.title"
                  v-tooltip.top="label.title"
                  class="prev-label"
                >
                  <span
                    class="prev-label__dot"
                    :style="{ background: label.color }"
                  />
                  <span class="prev-label__text">{{ label.title }}</span>
                </span>
              </div>
              <button
                v-if="overflowingLabels[conv.id]"
                class="prev-conv__labels-toggle"
                :aria-label="
                  expandedLabels[conv.id]
                    ? $t('CONVERSATION.CARD.HIDE_LABELS')
                    : $t('CONVERSATION.CARD.SHOW_LABELS')
                "
                @click.stop="toggleLabels(conv.id)"
              >
                <fluent-icon
                  :icon="
                    expandedLabels[conv.id] ? 'chevron-left' : 'chevron-right'
                  "
                  size="14"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
    <spinner v-else />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Spinner from 'shared/components/Spinner';
import agentMixin from '../../../mixins/agentMixin';
import timeMixin from 'dashboard/mixins/time';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';

// Paleta de fallback p/ marcadores sem cor cadastrada — mantém as pills
// diferenciáveis entre si (AC: cores diferenciáveis / futura categorização)
const FALLBACK_LABEL_COLORS = [
  '#01c6d8',
  '#1f9d63',
  '#d98014',
  '#7a5cf0',
  '#e34c8b',
  '#3b82c4',
];

export default {
  components: {
    Spinner,
  },
  mixins: [agentMixin, timeMixin],
  props: {
    contactId: {
      type: [String, Number],
      required: true,
    },
    conversationId: {
      type: [String, Number],
      required: true,
    },
  },
  data() {
    return {
      // estado por conversa (chave = id da conversa)
      expandedLabels: {},
      overflowingLabels: {},
    };
  },
  computed: {
    conversations() {
      const currentId = this.currentUser?.id;
      const currentConversations = this.$store.getters[
        'contactConversations/getContactConversation'
      ](
        this.contactId,
        currentId,
        this.isLimitedAgent,
        !this.isPermissionSeeAllTeamConversation
      );

      return currentConversations;
    },
    previousConversations() {
      return this.conversations
        .map(conversation => {
          const live = this.$store.getters.getConversationById(conversation.id);
          return live ? { ...conversation, priority: live.priority } : conversation;
        })
        .sort((a, b) => b.id - a.id);
    },
    // Itens do histórico no estilo .prev-conv do protótipo
    prevConvItems() {
      const statusColors = {
        resolved: '#1f9d63',
        open: 'var(--color-woot)',
        pending: '#d98014',
        snoozed: '#7a5cf0',
      };
      const statusLabel = status =>
        this.$t(`CONTACT_PANEL.CONVERSATIONS.STATUS.${status}`);
      return this.previousConversations.map(c => {
        const msgs = c.messages || [];
        // Ignora mensagens de atividade (message_type 2) e template (3) no título
        const realMsgs = msgs.filter(
          m => m.message_type === 0 || m.message_type === 1
        );
        // Usa a PRIMEIRA mensagem real como "tópico" da conversa (igual ao protótipo)
        const topicMsg =
          realMsgs[0] || c.last_non_activity_message || {};
        const title =
          topicMsg.content ||
          this.$t('CONTACT_PANEL.CONVERSATIONS.UNTITLED', {
            id: c.display_id || c.id,
          });
        const timestamp = c.last_activity_at || c.created_at || topicMsg.created_at;
        const date = timestamp ? this.dynamicTime(timestamp) : '';
        const parts = [statusLabel(c.status), date];
        return {
          id: c.id,
          displayId: c.display_id || c.id,
          title,
          meta: parts.filter(Boolean).join(' · '),
          dotColor: statusColors[c.status] || 'var(--s-400)',
          labels: this.resolveLabels(c.labels),
        };
      });
    },
    ...mapGetters({
      uiFlags: 'contactConversations/getUIFlags',
      currentUser: 'getCurrentUser',
      accountId: 'getCurrentAccountId',
      accountLabels: 'labels/getLabels',
    }),
  },
  watch: {
    contactId(newContactId, prevContactId) {
      if (newContactId && newContactId !== prevContactId) {
        this.fetchConversations(newContactId);
      }
    },
    prevConvItems() {
      this.$nextTick(this.computeLabelsOverflow);
    },
  },
  mounted() {
    // Nota: O ContactPanel.vue pode chamar isso em paralelo
    // Se já estiver carregando via PromiseAll do pai, evita duplicar
    // Também evita recarregar se já tiver dados para este contactId (ex: abrir/fechar acordeão)
    if (!this.uiFlags.isFetching && this.conversations.length === 0) {
      this.fetchConversations(this.contactId);
    }
    // Garante que as cores dos marcadores estejam disponíveis
    if (!this.accountLabels.length) {
      this.$store.dispatch('labels/get');
    }
    this.$nextTick(this.computeLabelsOverflow);
    window.addEventListener('resize', this.computeLabelsOverflow);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.computeLabelsOverflow);
  },
  methods: {
    fetchConversations(contactId) {
      if (contactId) {
        this.$store.dispatch('contactConversations/get', contactId);
      }
    },
    // Converte os títulos de marcadores da conversa em { title, color },
    // buscando a cor cadastrada e caindo numa paleta de fallback estável
    resolveLabels(labelTitles) {
      if (!Array.isArray(labelTitles)) return [];
      return labelTitles.map((title, index) => {
        const saved = this.accountLabels.find(l => l.title === title);
        return {
          title,
          color:
            saved?.color ||
            FALLBACK_LABEL_COLORS[index % FALLBACK_LABEL_COLORS.length],
        };
      });
    },
    toggleLabels(convId) {
      this.$set(this.expandedLabels, convId, !this.expandedLabels[convId]);
    },
    // Detecta, por conversa, se as pills estouram a largura disponível
    // (estado recolhido em linha única) p/ exibir o indicador de expansão
    computeLabelsOverflow() {
      this.prevConvItems.forEach(conv => {
        if (!conv.labels.length) return;
        const ref = this.$refs[`labels-${conv.id}`];
        const el = Array.isArray(ref) ? ref[0] : ref;
        if (!el) return;
        // mede sempre no estado recolhido (nowrap) p/ um resultado estável
        const isOverflowing = el.scrollWidth > el.clientWidth + 1;
        if (this.overflowingLabels[conv.id] !== isOverflowing) {
          this.$set(this.overflowingLabels, conv.id, isOverflowing);
        }
      });
    },
    navigateToConversation(conv) {
      this.$router.push(
        frontendURL(
          conversationUrl({ accountId: this.accountId, id: conv.displayId })
        )
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.no-label-message {
  margin-bottom: var(--space-normal);
  color: var(--s-500);
}

.prev-conv {
  display: flex;
  gap: var(--space-small);
  padding: var(--space-small) 0;
  border-bottom: 1px solid var(--color-border);
  cursor: pointer;

  &:last-child {
    border-bottom: 0;
  }

  &:hover .prev-conv__body b {
    color: var(--color-woot);
  }
}

.prev-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 5px;
  flex: none;
}

.prev-conv__body {
  min-width: 0;
  flex: 1;

  b {
    display: block;
    font-size: var(--font-size-small);
    font-weight: var(--font-weight-medium);
    color: var(--s-800);
  }

  small {
    display: block;
    margin-top: 2px;
    font-size: var(--font-size-mini);
    color: var(--s-400);
  }
}

// Linha dos marcadores: pills recolhidas em uma linha + botão de expandir
.prev-conv__labels-row {
  display: flex;
  align-items: center;
  gap: var(--space-smaller);
  margin-top: var(--space-smaller);
  min-width: 0;
}

.prev-conv__labels {
  display: flex;
  align-items: center;
  gap: var(--space-smaller);
  flex-wrap: nowrap;
  overflow: hidden;
  min-width: 0;

  &.is-expanded {
    flex-wrap: wrap;
    overflow: visible;
  }
}

.prev-label {
  display: inline-flex;
  align-items: center;
  gap: var(--space-smaller);
  max-width: 100%;
  padding: 2px var(--space-smaller);
  border-radius: 100px;
  background: var(--s-50);
  border: 1px solid var(--color-border);
  flex: none;

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex: none;
  }

  &__text {
    font-size: var(--font-size-nano);
    line-height: 1.4;
    color: var(--s-700);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.prev-conv__labels-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: none;
  padding: 0;
  width: var(--space-medium);
  height: var(--space-medium);
  border-radius: var(--border-radius-normal);
  border: 1px solid var(--color-border);
  background: var(--color-surface);
  color: var(--s-600);
  cursor: pointer;

  &:hover {
    color: var(--color-woot);
    border-color: var(--color-woot);
  }
}
</style>
